<script setup>
import IconHeaderBack from '@/components/svg/IconHeaderBack.vue';
import IconHeaderSetting from '@/components/svg/IconHeaderSetting.vue';
import IconMenu from '@/components/svg/IconMenu.vue';
import MypageLnbTabMenu from '@/components/MypageLnbTabMenu.vue';
import { gnbMenuLinks } from '@/constants/menuLinks';
import {
  gnbContentTypeMap as contentTypeMap,
  userMenuContentTypeMap,
  nangmanCenterContentTypeMap,
} from '@/constants/logContentType';
import { addContentLogAPI } from '@/api/logServiceApi';
import { useRoute, useRouter } from 'vue-router';
import { watch, computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useMyInfoStore } from '@/store/modules/myinfo';
import { usePaymentsStore } from '@/store/modules/payments';
import { DEV_MODE } from '@/config/dev-mode';
import { clearMySajuState } from '@/composables/calendar/mySaju/useMySaju';
import { useClientType } from '@/composables/useClientType';
import { useRnBridge } from '@/composables/useRnBridge';
import { useAppBack, useBackLayer } from '@/composables/useAppBack';
import { useLoginStore } from '@/store/modules/login';
import DropdownCreditInfo from '@/components/common/DropdownCreditInfo.vue';
import notificationWhiteOff from '@/assets/images/header-icons/notification_white_off.png';
import notificationWhiteOn from '@/assets/images/header-icons/notification_white_on.png';
import notificationBlackOff from '@/assets/images/header-icons/notification_off.png';
import notificationBlackOn from '@/assets/images/header-icons/notification_on.png';
import menuWhiteIcon from '@/assets/images/header-icons/menu_white.png';
import menuBlackIcon from '@/assets/images/header-icons/menu.png';
import closeIcon from '@/assets/images/header-icons/close.png';
import settingIcon from '@/assets/images/header-icons/setting.png';
import { getPartnerMeta } from '@/constants/partner';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  showMenu: {
    type: Boolean,
    default: false,
  },
  isSearched: {
    type: Boolean,
    default: false,
  },
  showBack: {
    type: Boolean,
    default: true,
  },
  showSettings: {
    type: Boolean,
    default: false,
  },
  sortMode: {
    type: String,
    default: 'desc',
  },
  // saju-result 전용: 한자/한글, 색상변환, 순서변경 버튼
  showSajuTools: {
    type: Boolean,
    default: false,
  },
  showBackWithSajuTools: {
    type: Boolean,
    default: false,
  },
  langMode: {
    type: String,
    default: 'CHNKOR',
  },
  colorMode: {
    type: String,
    default: 'fntcolor01',
  },
  showSave: {
    type: Boolean,
    default: true,
  },
  transparent: {
    type: Boolean,
    default: false,
  },
  showLogo: {
    type: Boolean,
    default: false,
  },
  logoWhite: {
    type: Boolean,
    default: false,
  },
  hideTitle: {
    type: Boolean,
    default: false,
  },
  showPartnerLogo: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'back',
  'save',
  'share',
  'settings',
  'sortChange',
  'searchEowayTrans',
  'searchEowayTransColor',
  'notification',
]);

const route = useRoute();
const { isApp, serverType } = useClientType();
const router = useRouter();
const store = useStore();
const myInfoStore = useMyInfoStore();

// [DEV] drawer 개발용 버튼 노출 (운영 앱에선 숨김 — main의 showDevPopupBtn과 동일 가드)
const showDevDrawerBtn = computed(
  () => DEV_MODE && (!isApp.value || serverType.value !== 'production'),
);
const paymentsStore = usePaymentsStore();
const { sendMessageToApp } = useRnBridge();
const { performOneBack } = useAppBack();
const loginStore = useLoginStore();

/** 제휴 파트너 메타데이터 (로고 URL, 표시 이름). 미등록 파트너면 null */
const partnerMeta = computed(() => getPartnerMeta(myInfoStore.partner?.provider));

/**
 * logoWhite prop에 따라 파트너 wordmark도 흰/검정 변형으로 동기화.
 * 낭만만세력 logo_b/logo_w와 동일한 토글 컨텍스트.
 */
const partnerLogoUrl = computed(() => {
  if (!partnerMeta.value) return null;
  return props.logoWhite ? partnerMeta.value.logoUrlInverted : partnerMeta.value.logoUrl;
});

// transparent 여부에 따른 아이콘 전환
const notificationIcon = computed(() => {
  if (props.transparent)
    return myInfoStore.hasUnreadAlarm ? notificationWhiteOn : notificationWhiteOff;
  return myInfoStore.hasUnreadAlarm ? notificationBlackOn : notificationBlackOff;
});
const menuIcon = computed(() => (props.transparent ? menuWhiteIcon : menuBlackIcon));

// 로그인 상태 확인
const isLoggedIn = computed(() => {
  const userId = store.getters['user/id'];
  const userName = store.getters['user/name'];
  try {
    const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || 'null');
    return !!(userId || userName || userInfo?.name);
  } catch {
    return !!(userId || userName);
  }
});

// 사용자 이름 가져오기
const displayName = computed(() => {
  if (isLoggedIn.value) {
    return myInfoStore?.displayName || store.getters['user/name'] || '';
  }
  return '';
});

const isHomeRoute = computed(() => route.path === '/main' || route.path === '/');

// 로그인 버튼 클릭
const handleLogin = () => {
  addContentLogAPI('gnb_btn_join');
  router.push({
    path: '/auth/login',
    query: { to: route.fullPath },
  });
  closeMypageMenu();
};

// 마이페이지 메뉴 관련
const isMypageMenuOpen = ref(false);
const isDrawerClosing = ref(false);

// 앱 drawer 열림 시 배경 스크롤 잠금
const unlockBodyScroll = () => {
  document.documentElement.style.overflow = '';
  document.body.style.overflow = '';
  document.body.style.touchAction = '';
};
watch(
  () => isApp.value && isMypageMenuOpen.value,
  (locked) => {
    if (locked) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      unlockBodyScroll();
    }
  },
);
onUnmounted(unlockBodyScroll);

const toggleMypageMenu = () => {
  if (isMypageMenuOpen.value) {
    closeMypageMenu();
  } else {
    isMypageMenuOpen.value = true;
  }
};

const closeMypageMenu = () => {
  if (isApp.value && isMypageMenuOpen.value) {
    isDrawerClosing.value = true;
    return new Promise((resolve) => {
      setTimeout(() => {
        isMypageMenuOpen.value = false;
        isDrawerClosing.value = false;
        resolve();
      }, 250);
    });
  } else {
    isMypageMenuOpen.value = false;
    return Promise.resolve();
  }
};

useBackLayer({
  id: 'mobile-header-drawer',
  isOpen: computed(() => isApp.value && isMypageMenuOpen.value),
  closeFromBack: closeMypageMenu,
});

// 앱에서 포커스 해제 시 메뉴 닫기
onMounted(() => {
  window.addEventListener('rn:closeMenu', closeMypageMenu);
});
onUnmounted(() => {
  window.removeEventListener('rn:closeMenu', closeMypageMenu);
});

// 마이페이지 메뉴 열릴 때 분석권/포인트 데이터 갱신
watch(isMypageMenuOpen, (open) => {
  if (open && isLoggedIn.value) {
    paymentsStore.fetchTicketCount();
    paymentsStore.getOwnPoint();
  }
});

// 라우터 변경 감지하여 마이페이지 메뉴 닫기
watch(
  () => route.path,
  () => {
    if (isMypageMenuOpen.value) {
      isMypageMenuOpen.value = false;
    }
  },
);

const handleBack = async () => {
  if (isApp.value) {
    await performOneBack();
    return;
  }
  emit('back');
};

// 메뉴 active 체크
const isMenuActive = (menuLink) => {
  const currentPath = route.path;
  switch (menuLink.name) {
    case '택일달력':
      return (
        currentPath.startsWith('/selection') ||
        currentPath.startsWith('/mobile/selection') ||
        currentPath.startsWith('/calendar/perpetual') ||
        currentPath.startsWith('/mobile/calendar/perpetual')
      );
    case '낭만일진':
      return (
        currentPath.startsWith('/fortune') ||
        currentPath.startsWith('/mobile/fortune') ||
        currentPath.startsWith('/calendar/fortune') ||
        currentPath.startsWith('/mobile/calendar/fortune')
      );
    case '낭만분석':
      return (
        currentPath.startsWith('/saju-service') ||
        currentPath.startsWith('/saju-result') ||
        currentPath.startsWith('/saju-insight') ||
        currentPath.startsWith('/analysis')
      );
    default:
      return currentPath.startsWith(menuLink.basePath || menuLink.link);
  }
};

const handleMenuClick = (menu) => {
  addContentLogAPI(contentTypeMap[menu.link]);
  router.push(menu.link);
  closeMypageMenu();
};

// 앱 드로어(LNB) 항목 클릭 로그 — PC와 동일 content_type 재사용(디바이스 구분은 백엔드 option2).
// 네비게이션은 router-link `to` 가 담당하므로, 여기서는 로그 발화 + 메뉴 닫기만 수행한다.
const drawerContentTypeMap = {
  ...userMenuContentTypeMap,
  ...nangmanCenterContentTypeMap,
  '/mypage/history': 'UM_point_usage_history_btn',
  '/about-nangman': 'gnb_btn_service',
  '/purchase/tickets': 'S_ticket_purchase_btn',
};
const handleDrawerNav = (path) => {
  const contentType = drawerContentTypeMap[path];
  if (contentType) addContentLogAPI(contentType);
  closeMypageMenu();
};

const handleLogout = async () => {
  addContentLogAPI('pi_btn_logout');
  if (isApp.value) {
    sendMessageToApp({ action: 'logout' });
    return;
  }
  try {
    await store.dispatch('user/logout');
    clearMySajuState();
    await router.push('/auth/login');
    myInfoStore.resetSelectedOption();
    closeMypageMenu();
  } catch (error) {
    console.error('Logout error:', error);
  }
};

const handleGoHome = () => {
  closeMypageMenu();
  if (!isHomeRoute.value) {
    router.push('/main');
  }
};

const handleNotification = () => {
  if (!isLoggedIn.value) {
    loginStore.showLoginModal();
    return;
  }
  sendMessageToApp({ action: 'navigate', route: { screen: 'NotificationList' } });
};

const handleSetting = () => {
  if (!isLoggedIn.value) {
    loginStore.showLoginModal();
    return;
  }
  sendMessageToApp({ action: 'navigate', route: { screen: 'Setting' } });
};
</script>

<template>
  <div
    class="mobile-header"
    :class="{
      'mobile-header--transparent': transparent,
      'mobile-header--with-logo': showLogo,
      'mobile-header--logo-white': logoWhite,
    }"
    @touchmove.prevent
  >
    <button
      v-if="showSajuTools && showBackWithSajuTools"
      class="mobile-header__back mobile-header__back--with-tools"
      @click="handleBack"
    >
      <IconHeaderBack />
    </button>
    <!-- 사주결과 도구 버튼 모드 (saju-result 페이지): 한자/한글, 색상변환, 순서변경 -->
    <div
      v-if="showSajuTools"
      class="mobile-header__left saju-tools"
      :class="{ 'saju-tools--with-back': showBackWithSajuTools }"
    >
      <!-- 한자/한글 전환 -->
      <button
        class="mobile-header__tool-btn btn-rd btn-rd-38"
        :class="langMode === 'CHNKOR' ? 'chnkor' : langMode === 'CHN' ? 'chn' : 'kor'"
        @click="
          emit(
            'searchEowayTrans',
            langMode === 'CHNKOR' ? 'CHN' : langMode === 'CHN' ? 'KOR' : 'CHNKOR',
          )
        "
      />
      <!-- 색상 전환 -->
      <button
        class="mobile-header__tool-btn btn-rd btn-rd-38"
        :class="colorMode"
        @click="
          emit(
            'searchEowayTransColor',
            colorMode === 'fntcolor01'
              ? 'fntcolor02'
              : colorMode === 'fntcolor02'
                ? 'fntcolor03'
                : 'fntcolor01',
          )
        "
      />
      <!-- 순서 변경 -->
      <button
        class="mobile-header__tool-btn btn-rd btn-rd-38 sorting"
        :class="{ on: sortMode === 'asc' }"
        @click="emit('sortChange')"
      />
    </div>
    <!-- 설정 아이콘 모드 (사주입력 페이지) -->
    <div v-else-if="showSettings" class="mobile-header__left">
      <button class="mobile-header__icon-btn" @click="emit('settings')">
        <IconHeaderSetting />
      </button>
      <button
        v-if="isSearched"
        class="mobile-header__icon-btn sort-btn"
        :class="{ on: sortMode === 'asc' }"
        @click="emit('sortChange')"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M5 16H19M19 16L16.5 13.5M19 16L16.5 18.5"
            stroke="#333"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M19 8H5M5 8L7.5 5.5M5 8L7.5 10.5"
            :stroke="sortMode === 'asc' ? '#EB4C10' : '#333'"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
    <!-- 기본 뒤로가기 모드 -->
    <button v-else-if="showBack" class="mobile-header__back" @click="handleBack">
      <IconHeaderBack />
    </button>
    <div v-if="showPartnerLogo && partnerMeta" class="mobile-header__partner">
      <span class="mobile-header__partner-divider" aria-hidden="true" />
      <img :src="partnerLogoUrl" :alt="partnerMeta.name" class="mobile-header__partner-logo" />
    </div>
    <slot name="title-content">
      <div v-if="!hideTitle" class="mobile-header__title">{{ title }}</div>
    </slot>
    <div class="mobile-header__right">
      <slot name="right-prepend" />
      <!-- 앱 1.3.2+: 알림 아이콘 -->
      <button
        v-if="showMenu && isApp"
        class="mobile-header__notification"
        @click="handleNotification"
      >
        <img :src="notificationIcon" alt="알림" />
      </button>
      <button v-if="isSearched" class="mobile-header__share" @click="emit('share')">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 19.091 20">
          <path
            d="M7.537,11.2a2.372,2.372,0,0,0,0-2.129m0,2.129a2.383,2.383,0,1,1,0-2.129m0,2.129,5.294,2.63M7.537,9.074l5.294-2.63m0,0a2.4,2.4,0,0,0,3.213,1.064,2.373,2.373,0,0,0,1.071-3.193A2.4,2.4,0,0,0,13.9,3.251a2.373,2.373,0,0,0-1.071,3.193Zm0,7.389A2.373,2.373,0,0,0,13.9,17.026a2.4,2.4,0,0,0,3.214-1.064,2.373,2.373,0,0,0-1.071-3.193,2.4,2.4,0,0,0-3.214,1.064Z"
            transform="translate(-0.533 -0.499)"
            fill="none"
            stroke="#23252e"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="1.5"
          />
        </svg>
      </button>
      <button v-if="isSearched && showSave" class="mobile-header__save" @click="emit('save')">
        저장
      </button>
      <!-- 앱 1.3.2+: 드로어 아이콘 / 웹: 마이페이지 아이콘 -->
      <button v-if="showMenu && isApp" class="mobile-header__drawer" @click="toggleMypageMenu">
        <img :src="menuIcon" alt="메뉴" />
      </button>
      <button v-if="showMenu && !isApp" class="mobile-header__mypage" @click="toggleMypageMenu">
        <i class="icon-mypage"></i>
      </button>
    </div>
  </div>

  <!-- 마이페이지 메뉴 레이어 (PC 스타일 드롭다운) -->
  <Teleport to="body">
    <div
      v-if="isMypageMenuOpen && showMenu"
      :class="['mypage-menu-overlay', { 'mypage-menu-overlay--drawer': isApp }]"
      @click="closeMypageMenu"
    >
      <div
        :class="[
          'mypage-menu-dropdown',
          {
            'mypage-menu-dropdown--drawer': isApp,
            'mypage-menu-dropdown--closing': isDrawerClosing,
          },
        ]"
        @click.stop
      >
        <!-- 드로어 헤더: 닫기 / 알림 / 설정 (앱 1.3.2+) -->
        <div v-if="isApp" class="drawer-header">
          <button class="drawer-header__btn" @click="closeMypageMenu">
            <img :src="closeIcon" alt="닫기" />
          </button>
          <div class="drawer-header__right">
            <button class="drawer-header__btn" @click="handleNotification">
              <img
                :src="myInfoStore.hasUnreadAlarm ? notificationBlackOn : notificationBlackOff"
                alt="알림"
              />
            </button>
            <button class="drawer-header__btn" @click="handleSetting">
              <img :src="settingIcon" alt="설정" />
            </button>
          </div>
        </div>

        <div class="login-msg-box" @click="isTicketDropdownOpen = false">
          <button
            v-if="!isApp"
            class="btn-home"
            :class="{ active: isHomeRoute }"
            @click="handleGoHome"
          >
            <i class="icon-home"></i>홈
          </button>
          <button v-if="isApp && isLoggedIn" class="btn-home logout" @click="handleLogout">
            로그아웃
          </button>

          <!-- 로그인된 사용자 -->
          <template v-if="isLoggedIn">
            <div class="txt-box">
              <span>
                <span class="txt1">{{ displayName || '게스트' }}</span> 님
              </span>
              <span class="txt2">행복한 하루 되세요</span>
            </div>
          </template>
          <!-- 게스트 (비로그인) -->
          <template v-else>
            <div class="txt-box">
              <span> <span class="txt1">게스트</span> 님 </span>
              <span class="txt2">로그인 후 이용해주세요</span>
            </div>
          </template>

          <!-- 분석권/포인트 (로그인 사용자만) -->
          <DropdownCreditInfo
            v-if="isLoggedIn || isApp"
            :isLoggedIn="isLoggedIn"
            :visible="isMypageMenuOpen"
            charge-log-source="앱드로어"
            @close-menu="closeMypageMenu"
          />

          <!-- 서비스 구매 (앱 drawer 전용) -->
          <router-link
            v-if="isApp"
            to="/purchase/tickets"
            class="drawer-service-purchase"
            @click="handleDrawerNav('/purchase/tickets')"
          >
            <i class="drawer-service-purchase__icon"></i>
            <div class="drawer-service-purchase__text">
              <span class="drawer-service-purchase__title">서비스 구매</span>
              <span class="drawer-service-purchase__desc"
                >여러 건을 묶어 더 알뜰하게 이용할 수 있어요!</span
              >
            </div>
          </router-link>

          <!-- GNB 메뉴 (햄버거 메뉴 항목들) — 앱 drawer에서는 MobileTabBar가 담당하므로 숨김 -->
          <div v-if="!isApp" class="gnb-menu-list">
            <template v-for="(menu, key) in gnbMenuLinks" :key="key">
              <router-link
                v-if="menu.name !== '낭만만세력은?'"
                :to="menu.link"
                :class="{ active: isMenuActive(menu) }"
                class="gnb-menu-item"
                @click="handleMenuClick(menu)"
              >
                {{ menu.name }}
              </router-link>
              <a
                v-else
                :href="$router.resolve(menu.link).href"
                target="_blank"
                rel="noopener"
                class="gnb-menu-item"
                @click="handleMenuClick(menu)"
              >
                {{ menu.name }}
              </a>
            </template>
          </div>

          <!-- 앱 drawer: 네이티브 DrawerMenu와 동일한 게시판 섹션 구조 -->
          <template v-if="isApp">
            <template v-if="isLoggedIn">
              <div class="drawer-section">
                <div class="drawer-section__title">마이페이지</div>
                <div class="drawer-section__grid">
                  <router-link
                    to="/mypage/myinfo"
                    class="drawer-section__item"
                    @click="handleDrawerNav('/mypage/myinfo')"
                  >
                    내 정보
                  </router-link>
                  <router-link
                    to="/mypage/moreinfo"
                    class="drawer-section__item"
                    @click="handleDrawerNav('/mypage/moreinfo')"
                  >
                    부가정보
                  </router-link>
                  <router-link
                    to="/mypage/history"
                    class="drawer-section__item"
                    @click="handleDrawerNav('/mypage/history')"
                  >
                    이용중인 서비스
                  </router-link>
                  <router-link
                    v-if="DEV_MODE || myInfoStore.isSmartwelMember"
                    to="/mypage/subscription"
                    class="drawer-section__item"
                    @click="handleDrawerNav('/mypage/subscription')"
                  >
                    구독 관리
                  </router-link>
                </div>
              </div>
            </template>
            <div class="drawer-section">
              <div class="drawer-section__title">낭만센터</div>
              <div class="drawer-section__grid">
                <router-link
                  to="/nangman-center/notice"
                  class="drawer-section__item"
                  @click="handleDrawerNav('/nangman-center/notice')"
                  >공지사항</router-link
                >
                <router-link
                  to="/nangman-center/event"
                  class="drawer-section__item"
                  @click="handleDrawerNav('/nangman-center/event')"
                  >이벤트</router-link
                >
                <router-link
                  to="/nangman-center/word"
                  class="drawer-section__item"
                  @click="handleDrawerNav('/nangman-center/word')"
                  >생활 속 명리</router-link
                >
                <router-link
                  to="/nangman-center/essay"
                  class="drawer-section__item"
                  @click="handleDrawerNav('/nangman-center/essay')"
                  >낭만에세이</router-link
                >
                <router-link
                  to="/nangman-center/faq"
                  class="drawer-section__item"
                  @click="handleDrawerNav('/nangman-center/faq')"
                  >자주 묻는 질문</router-link
                >
                <a
                  :href="$router.resolve('/about-nangman').href"
                  target="_blank"
                  rel="noopener"
                  class="drawer-section__item"
                  @click="handleDrawerNav('/about-nangman')"
                  >낭만만세력은?</a
                >
              </div>
            </div>
            <template v-if="isLoggedIn">
              <div class="drawer-section">
                <div class="drawer-section__title">문의/신청</div>
                <div class="drawer-section__grid">
                  <router-link
                    to="/mypage/inquiry"
                    class="drawer-section__item"
                    @click="handleDrawerNav('/mypage/inquiry')"
                    >1:1문의/신청</router-link
                  >
                  <router-link
                    to="/mypage/inquiryList"
                    class="drawer-section__item"
                    @click="handleDrawerNav('/mypage/inquiryList')"
                    >내역보기</router-link
                  >
                </div>
              </div>
            </template>
            <template v-else>
              <button class="btn-xs line login-btn" @click="handleLogin">로그인</button>
            </template>

            <!-- [DEV] 개발용 (운영 앱에선 숨김) -->
            <div v-if="showDevDrawerBtn" class="drawer-section">
              <div class="drawer-section__title">[DEV]</div>
              <div class="drawer-section__grid">
                <button
                  type="button"
                  class="drawer-section__item"
                  @click="
                    closeMypageMenu();
                    router.push('/partner/smartwel?preview=done');
                  "
                >
                  구독안내
                </button>
              </div>
            </div>
          </template>

          <!-- 모바일 웹: 기존 MypageLnbTabMenu 유지 -->
          <template v-else>
            <template v-if="isLoggedIn">
              <MypageLnbTabMenu />
              <button class="btn-xs line" @click="handleLogout">로그아웃</button>
            </template>
            <template v-else>
              <button class="btn-xs line login-btn" @click="handleLogin">로그인</button>
            </template>
          </template>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss">
// ─── 기본 스타일 (display: none, 속성은 미리 정의) ───

.mobile-header {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  height: calc(52px + var(--safe-area-top, 0px));
  padding-top: var(--safe-area-top, 0px);
  align-items: center;
  justify-content: center;
  background: #fff;
  border-bottom: 1px solid #ebebeb;
}

.mobile-header.mobile-header--transparent {
  background: transparent;
  border-bottom-color: transparent;
  box-shadow: none;
}

.mobile-header.mobile-header--with-logo::before {
  content: '';
  position: absolute;
  left: 16px;
  /* 오프셋: safe-area-top 있으면 -6px, 없으면 -4px (min으로 0/2px 토글) */
  top: calc((100% + var(--safe-area-top, 0px)) / 2 - 4px - min(var(--safe-area-top, 0px), 2px));
  width: 130px;
  height: 40px;
  transform: translateY(-50%);
  background: url('@manseImages/logo_b.png') left center no-repeat;
  background-size: auto 100%;
  pointer-events: none;
}

.mobile-header.mobile-header--with-logo.mobile-header--logo-white::before {
  background-image: url('@manseImages/logo_w.png');
}

.mobile-header__partner {
  position: absolute;
  left: calc(16px + 108px + 2px);
  /* 오프셋: safe-area-top 있으면 -8px, 없으면 -6px (min으로 0/2px 토글) */
  top: calc((100% + var(--safe-area-top, 0px)) / 2 - 6px - min(var(--safe-area-top, 0px), 2px));
  display: flex;
  align-items: center;
  gap: 8px;
  transform: translateY(-50%);
  pointer-events: none;
}

.mobile-header__partner-divider {
  width: 1px;
  height: 22px;
  background: #d9d9d9;
  position: relative;
  top: 8px;
}

.mobile-header--transparent .mobile-header__partner-divider {
  background: rgba(255, 255, 255, 0.5);
}

.mobile-header__partner-logo {
  height: 24px;
  width: auto;
  display: block;
  position: relative;
  top: 8px;
}

@media (max-width: 380px) {
  .mobile-header__partner {
    gap: 6px;
    left: calc(16px + 108px);
  }
  .mobile-header__partner-divider {
    height: 18px;
    top: 7px;
  }
  .mobile-header__partner-logo {
    height: 20px;
    top: 7px;
  }
}

.mobile-header__title {
  color: #333;
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px;
  text-align: center;
}

.mobile-header__back {
  position: absolute;
  bottom: 26px;
  left: 16px;
  transform: translateY(50%);
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;

  .icon {
    width: 24px;
    height: 24px;
  }
}

.mobile-header__back.mobile-header__back--with-tools {
  z-index: 2;
  // transform: none;
}

.mobile-header__left {
  position: absolute;
  left: 16px;
  display: flex;
  align-items: center;
  gap: 12px;

  &.saju-tools {
    gap: 14px;
    left: 8px;

    &.saju-tools--with-back {
      left: 56px;
    }

    .mobile-header__tool-btn.btn-rd.btn-rd-38 {
      width: 30px !important;
      height: 30px !important;
      min-width: 30px;
      min-height: 30px;
      margin-left: 0 !important;
      border: none;
      cursor: pointer;
      padding: 0;
      box-sizing: border-box;
      background-color: #fff;
      background-size: 100% 100% !important;
      background-position: center center;
      background-repeat: no-repeat;
      font-size: 0;
      border-radius: 100%;
    }
  }
}

.mobile-header__icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;

  .icon {
    width: 24px;
    height: 24px;
  }
}

.mobile-header__right {
  position: absolute;
  right: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 1001;
}

.mobile-header__share {
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;

  img {
    width: 20px;
    height: 20px;
  }
}

.mobile-header__save {
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  color: #eb4c10;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 600;
}

.mobile-header__notification,
.mobile-header__drawer {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;

  img {
    width: 24px;
    height: 24px;
  }
}

// 기본값 숨김
.mobile-header__menu {
  display: none;
}

.mobile-header__mypage {
  display: none;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;

  .icon-mypage {
    display: flex;
    background: url('@manseImages/icon-mypage.png') center center no-repeat;
    background-size: 32px auto;
    width: 32px;
    height: 32px;
    border-radius: 100%;
    font-size: 0;
  }
}

.mobile-menu-overlay,
.mobile-menu-layer {
  display: none;
}

.mypage-menu-overlay {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  z-index: 9998 !important;

  &.mypage-menu-overlay--drawer {
    background: rgba(0, 0, 0, 0.4);
  }
}

@keyframes slideOutRight {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

.mypage-menu-dropdown {
  display: none;
  position: fixed;
  top: 60px;
  right: 16px;
  width: 280px;
  max-height: calc(100dvh - 76px);
  overflow-y: auto;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 9999 !important;

  // 앱 드로어 모드
  &.mypage-menu-dropdown--drawer {
    top: 0;
    right: 0;
    bottom: 0;
    width: min(95vw, 400px);
    max-height: 100vh;
    border-radius: 0;
    box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
    animation: slideInRight 0.25s ease-out;
    display: flex;
    flex-direction: column;

    &.mypage-menu-dropdown--closing {
      animation: slideOutRight 0.25s ease-in forwards;
    }

    .login-msg-box {
      padding: 16px 16px calc(var(--safe-area-bottom, 0) + 16px);
      width: 100%;
      display: flex;
      flex: 1;
      flex-direction: column;
      align-items: flex-start;
      overflow-y: auto;

      .btn-home.logout {
        top: 16px;
        right: 16px;
        padding: 6px 10px;
        height: fit-content;
        border: 1px solid #e6e6e6;
        border-radius: 6px;
        background: #fff;
        color: #333;
        font-size: 13px;
        font-weight: 700;
      }

      .txt-box {
        padding: 0;
        font-size: 20px;
        line-height: 24px;

        .txt1 {
          font-size: 20px;
          line-height: 24px;
        }
        .txt2 {
          font-size: 16px;
          line-height: 24px;
        }
      }

      .credit-info-row {
        width: 100%;
        gap: 8px;

        .credit-info-card {
          padding: 12px 16px;

          &--ticket {
            min-width: 120px;
          }

          &__label {
            font-size: 13px;
          }

          &__value {
            font-size: 16px;
            line-height: 22px;
          }

          &__charge-btn {
            font-size: 13px;
          }
        }

        .ticket-dropdown {
          &__header {
            color: #333;
            font-size: 15px;
            font-weight: 500;
            line-height: 24px;
          }

          &__dot {
            width: 6px;
            color: #333;
            font-size: 18px;
            font-weight: 900;
          }

          &__right {
            padding-right: 16px;
            color: #eb4c10;
            font-size: 15px;
            font-weight: 700;

            &:has(.icon) {
              padding-right: 0;
            }
          }

          &__arrow-icon {
            display: flex;
            width: 12px;
            height: 12px;
          }

          &__sub {
            padding: 0 16px 0 12px;
            li {
              font-size: 13px;
              font-weight: 500;
              color: #575757;
              line-height: 20px;
            }
          }
        }
      }

      .btn-xs.login-btn {
        height: 44px;
        font-size: 16px;
        font-weight: 700;
      }

      .btn-xs.login-btn {
        margin-top: auto;
      }
    }

    .drawer-section {
      margin-top: 24px;
      padding: 0 4px;
      width: 100%;
      box-sizing: border-box;

      &__title {
        color: #949494;
        font-family: Pretendard, sans-serif;
        font-size: 13px;
        font-weight: 700;
        line-height: 18px;
        margin-bottom: 8px;
      }

      &__grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 4px 20px;
      }

      &__item {
        padding: 8px 0;
        color: #333;
        font-family: Pretendard, sans-serif;
        font-size: 15px;
        font-weight: 700;
        line-height: 20px;
        text-decoration: none;

        &:active {
          opacity: 0.6;
        }
      }
    }

    .drawer-section + .drawer-section {
      margin-top: 24px;
    }

    .drawer-section + .btn-xs {
      margin-top: 32px;
    }

    .drawer-service-purchase {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 12px;
      width: 100%;
      padding: 12px;
      border-radius: 12px;
      border: 1px solid #ffc326;
      background: #fff;
      box-sizing: border-box;
      text-decoration: none;
      margin-bottom: 8px;

      &__icon {
        flex-shrink: 0;
        width: 24px;
        height: 24px;
        background: url('@manseImages/ticket-icon.svg') center center no-repeat;
        background-size: contain;
      }
      &__text {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
      &__title {
        color: #333;
        font-family: Pretendard, sans-serif;
        font-size: 15px;
        font-weight: 700;
        line-height: 20px;
      }
      &__desc {
        color: #575757;
        font-family: Pretendard, sans-serif;
        font-size: 13px;
        font-weight: 400;
        line-height: 16px;
      }
    }
  }

  .drawer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    padding-top: calc(12px + var(--safe-area-top, 0px));
    border-bottom: none;
  }

  .drawer-header__right {
    display: flex;
    gap: 8px;
  }

  .drawer-header__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 0;

    img {
      width: 24px;
      height: 24px;
    }
  }

  .login-msg-box {
    position: relative;
    padding: 20px;
    width: 100% !important;
    box-sizing: border-box;

    .btn-home {
      position: absolute;
      top: 14px;
      right: 14px;
      display: inline-flex;
      align-items: center;
      gap: 4px;
      height: 28px;
      padding: 0 8px;
      border: 1px solid #e6e6e6;
      border-radius: 14px;
      background: #fff;
      color: #333;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;

      .icon-home {
        width: 14px;
        height: 14px;
        background-color: currentColor;
        -webkit-mask: url('@manseImages/icon_home.svg') center center / contain no-repeat;
        mask: url('@manseImages/icon_home.svg') center center / contain no-repeat;
      }

      &.active {
        color: #eb4c10;
        border-color: #f7d1c3;
        background: #fff5f2;
      }
    }

    .txt-box {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
      color: #333;
      font-size: 15px;
      margin-bottom: 16px;
      padding-top: 6px;

      .txt1 {
        font-weight: 700;
      }

      .txt2 {
        color: #757575;
        font-size: 13px;
        margin-top: 4px;
      }
    }

    .gnb-menu-list {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 16px;
      padding-top: 12px;
      border-top: 1px solid #f5f5f5;

      .gnb-menu-item {
        display: inline-block;
        padding: 6px 12px;
        color: #333;
        font-family: Pretendard;
        font-size: 13px;
        font-weight: 500;
        line-height: 20px;
        text-decoration: none;
        border-radius: 6px;
        background: #f9f9f9;
        transition: all 0.2s;
        white-space: nowrap;

        &:hover {
          background: #f0f0f0;
          color: #eb4c10;
        }

        &.active {
          background: #fff5f2;
          color: #eb4c10;
          font-weight: 600;
        }
      }
    }

    .gnb-menu-box {
      border-top: 1px solid #f5f5f5;
      padding-top: 12px;

      .depth01 {
        margin-bottom: 12px !important;

        .gnb-link {
          font-size: 13px !important;
          line-height: 18px !important;
          white-space: nowrap;

          svg {
            width: 16px;
            height: 16px;
            flex-shrink: 0;
          }
        }

        .sub-menu {
          margin-left: 24px !important;
          margin-top: 4px !important;

          a {
            font-size: 13px !important;
            padding: 3px 0 !important;
          }
        }
      }
    }

    .btn-xs.line {
      width: 100%;
      margin-top: 8px;
      padding: 8px 16px;
      border: 1px solid #ebebeb;
      border-radius: 6px;
      background: #fff;
      color: #333;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: #f5f5f5;
      }

      &.login-btn {
        background: var(--orange-300-main, #eb4c10);
        color: #fff;
        border-color: var(--orange-300-main, #eb4c10);

        &:hover {
          background: #d43d0e;
          border-color: #d43d0e;
        }
      }
    }
  }
}

// ─── 768px 이하: display 전환만 ───

@media (max-width: 768px) {
  .mobile-header {
    display: flex;
  }

  .mobile-header__mypage {
    display: flex;
  }

  .mypage-menu-overlay {
    display: block;
  }

  .mypage-menu-dropdown {
    display: block;
  }
}
</style>
